import { ref } from 'vue';
import { useApi } from '@/composables/useAppGlobals';
import type { TaskSummary } from '@/types/api';

export interface TaskFilters {
  name?: string;
  assignee?: string;
  state?: string | null;
}

function toApiParams(filters: TaskFilters, cursor?: string) {
  return {
    cursor: cursor || undefined,
    name: filters.name || undefined,
    assignee: filters.assignee && filters.assignee !== 'all' ? filters.assignee : undefined,
    state: filters.state || undefined,
  };
}

export function useTaskPagination() {
  const api = useApi();
  const slides = ref<TaskSummary[]>([]);
  const nextCursor = ref<string | null>(null);
  const hasMore = ref(false);
  const isBusy = ref(false);
  const isLoadingMore = ref(false);

  async function loadTasks(filters: TaskFilters = {}) {
    isBusy.value = true;
    try {
      const result = await api.getSlides(toApiParams(filters));
      slides.value = result.items;
      nextCursor.value = result.next_cursor;
      hasMore.value = result.has_more;
    } finally {
      isBusy.value = false;
    }
  }

  async function loadMore(filters: TaskFilters = {}) {
    if (!hasMore.value || isBusy.value || isLoadingMore.value) return;
    isLoadingMore.value = true;
    try {
      const result = await api.getSlides(toApiParams(filters, nextCursor.value ?? undefined));
      slides.value = [...slides.value, ...result.items];
      nextCursor.value = result.next_cursor;
      hasMore.value = result.has_more;
    } finally {
      isLoadingMore.value = false;
    }
  }

  function patchSlide(id: string, patch: Partial<TaskSummary>) {
    const idx = slides.value.findIndex(s => s.id === id);
    if (idx !== -1) slides.value[idx] = { ...slides.value[idx], ...patch };
  }

  function removeSlide(id: string) {
    slides.value = slides.value.filter(s => s.id !== id);
  }

  return { slides, hasMore, isBusy, isLoadingMore, loadTasks, loadMore, patchSlide, removeSlide };
}
